import { solutionInteractServiceModule } from '../solution-interact-service/solution-interact-service';
import { appAlertComponentModule } from '../../components/app-alert/app-alert';
import { isString } from '../../utils/type-utils';
import * as ArrayUtils from '../../utils/array-utils';

const MAX_AUTO_OPENED_FILES = 4;

export const multiFileChallengeServiceInteractModule = angular
  .module('Q.services.multi-file-challenge-service-interact', [
    solutionInteractServiceModule,
    appAlertComponentModule,
  ])

  .factory(
    'multiFileChallengeServiceInteract',
    function (
      $rootScope,
      solutionInteractService,
      appAlert,
      $timeout,
      $interval,
      $document,
    ) {
      function setupInteract(svc) {
        svc.startInteract = (challengeRef, liveInterview) => {
          if (svc.interact) {
            if (svc.interact.isDisconnected) {
              svc.interact.reconnect();
            }
            return;
          }

          const interact = (svc.interact = solutionInteractService.start(
            challengeRef,
            liveInterview,
            svc.solution,
          ));
          svc._mfcListeners.push(interact.destroy);

          let _fileToOpen = null;
          const autoOpenedFiles = [];

          /**
           * Updates a file if it exists, and opens it if we are following the current editor.
           * If the file doesn't exist, we track it, and attempt again if any file gets updated.
           * @param {string} id
           * @param {Object?} properties
           * @private
           */
          function _updateAndOpenFile({ id, properties = {} }) {
            _fileToOpen = null;
            const { contents, selections } = properties;
            const file = svc.findFile(id);
            if (file) {
              if (isString(contents)) {
                file.contents = contents;
              }
              svc.withFileLayout((fileLayout) => {
                const wasOpen = svc.openFiles.includes(file);
                if (svc.interact.followEditor && !svc.interact.privateEditing) {
                  fileLayout.open(file, { cursor: selections });
                  svc.workspace && svc.workspace.hideAllPopups();
                  if (!wasOpen) {
                    // the next two lines add or move the file to the front of the autoOpen queue
                    ArrayUtils.remove(autoOpenedFiles, file);
                    autoOpenedFiles.push(file);
                    // only preserve the last 4 auto-opened files
                    while (autoOpenedFiles.length > MAX_AUTO_OPENED_FILES) {
                      fileLayout.close(autoOpenedFiles.shift());
                    }
                  }
                } else if (wasOpen && selections) {
                  // just update cursor information in the background
                  fileLayout.setCursor(file, selections);
                }
              });
              svc.updateWebPreview(true);
            } else {
              _fileToOpen = { id, properties };
            }
          }

          // If false, we don't automatically open and close files
          interact.followEditor = true;
          interact.privateEditing = false;
          interact.toggleFollowEditor = (newVal = !interact.followEditor) => {
            if (interact.privateEditing) {
              interact.privateEditing = false;
              newVal = true;
            }
            interact.followEditor = newVal;
            if (newVal) {
              _updateAndOpenFile({ id: interact.activeFileId });
            }
          };

          //// MultiFileChallengeService listeners

          svc.on(
            'fileChange',
            (event, { file, selections, userInitiated } = {}) => {
              if (userInitiated && interact.canEdit && file && file.path) {
                if (!file.$hideFromInteract) {
                  interact.updateFile({
                    id: file.interactId,
                    properties: {
                      contents: file.contents,
                      selections: selections || [],
                    },
                  });
                }
              } else if (_fileToOpen) {
                _updateAndOpenFile(_fileToOpen);
              }
            },
          );

          const privateEditingFileIds = [
            'runnerframe',
            'idesettings',
            'webpreview',
            'candidatenotes',
            'reviews',
          ];

          let privateEditingTimeout = null;
          const _setPrivateEditing = (val) => {
            $timeout.cancel(privateEditingTimeout);
            interact.privateEditing = val;
            if (val) {
              // after 10 seconds, clear the flag if there's been no further focus changes
              privateEditingTimeout = $timeout(() => {
                interact.privateEditing = false;
              }, 10000);
            }
          };
          svc.on('tabFocused', (event, { file } = {}) => {
            // if we're focused on a tab that doesn't work with Interact, temporarily stop following
            _setPrivateEditing(
              file.$hideFromInteract || privateEditingFileIds.includes(file.id),
            );
          });
          // polls for iframe focus every second, assume we're editing something locally if an
          // iframe has focus
          const iframeFocusListener = $interval(() => {
            const activeElement = $document[0].activeElement;
            if (activeElement && activeElement.nodeName === 'IFRAME') {
              _setPrivateEditing(true);
            }
          }, 1000);
          svc._mfcListeners.push(() => {
            $interval.cancel(iframeFocusListener);
          });

          //// Model Listeners

          const solutionListener = $rootScope.$on(
            `socket:model.changed:Solution`,
            () => {
              if (_fileToOpen) {
                _updateAndOpenFile(_fileToOpen);
              }
            },
          );
          svc._mfcListeners.push(solutionListener);

          //// SolutionInteractService Listeners

          interact.on('connectionError', (event, { response }) => {
            appAlert.modelError(response, {
              prefix: 'There was an issue setting up Interact:',
            });
          });
          interact.on('activeFileIdChanged', (event, { activeFileId }) => {
            _updateAndOpenFile({ id: activeFileId });
          });
          interact.on(
            'activeEditorChanged',
            (/* event, { activeEditor } */) => {
              // update our read-only status
              svc.refreshOnFilesChanged();
            },
          );
          interact.on('canEditChanged', (event, { canEdit }) => {
            // hide all popups when we're not the active editor
            svc.workspace && svc.workspace.enablePopups(canEdit);
          });
          interact.on('fileChanged', (event, eventProperties) => {
            _updateAndOpenFile(eventProperties);
          });
          interact.on('filesChanged', (event, { ids }) => {
            ids.forEach((id) => {
              interact.getFile({ id }).then((interactFile) => {
                const file = svc.findFile(id);
                const contents =
                  interactFile.properties && interactFile.properties.contents;
                if (file && isString(contents)) {
                  file.contents = contents;
                }
              });
            });
          });
          interact.on('fileCreated', (event, { properties }) => {
            svc.createFile(properties, false, true, false);
          });
          interact.on('fileRenamed', (event, { oldPath, newPath }) => {
            const file = svc.findFile(oldPath);
            if (file) {
              svc.renameFile({ file, newPath });
            }
          });
          interact.on('fileDeleted', (event, { id }) => {
            const file = svc.findFile(id);
            if (file) {
              svc.delete(file, file.path, false, false);
            }
          });
        };
      }

      return {
        setupInteract,
      };
    },
  );
